import {
	configFromEnv,
	createHttpAgent,
	createProtocolActor,
	type ProtocolActor,
	type IcClientConfig,
} from "@motokomaker/ic-client";
import type { HttpAgent, Identity } from "@icp-sdk/core/agent";
import { getEnvMap, getIcConfig, hasProtocolEnv } from "@/lib/network";

let cachedAnon: ProtocolActor | null | undefined;
let cachedAnonAgent: HttpAgent | null | undefined;
let lastAttachError: string | null = null;

export { hasProtocolEnv };

export function getConfig(): IcClientConfig {
	return getIcConfig();
}

export { getIcConfig };

export function getLastAttachError(): string | null {
	return lastAttachError;
}

function formatAttachError(err: unknown): string {
	if (err instanceof Error && err.message) return err.message;
	return String(err ?? "unknown attach error");
}

/** Anonymous protocol actor for public queries. Null if env missing or create fails. */
export async function getV2ProtocolActor(): Promise<ProtocolActor | null> {
	if (cachedAnon !== undefined) return cachedAnon;
	if (!hasProtocolEnv()) {
		cachedAnon = null;
		cachedAnonAgent = null;
		lastAttachError = "Protocol canister ID not in SPA env";
		return null;
	}
	try {
		const env = getEnvMap();
		const config = configFromEnv({
			...env,
			VITE_CANISTER_ID_PROTOCOL:
				env.VITE_CANISTER_ID_PROTOCOL ?? env.VITE_PROTOCOL_CANISTER_ID,
		});
		const agent = await createHttpAgent(config);
		cachedAnonAgent = agent;
		cachedAnon = createProtocolActor(config.protocolCanisterId, agent);
		lastAttachError = null;
		return cachedAnon;
	} catch (e) {
		console.error("[motokomaker-v2] anonymous protocol actor failed:", e);
		lastAttachError = formatAttachError(e);
		cachedAnon = null;
		cachedAnonAgent = null;
		return null;
	}
}

export async function getAnonymousAgent(): Promise<HttpAgent | null> {
	await getV2ProtocolActor();
	return cachedAnonAgent ?? null;
}

export type AuthenticatedActorResult = {
	agent: HttpAgent;
	actor: ProtocolActor;
	config: IcClientConfig;
};

/**
 * Authenticated actor for updates.
 * Throws on failure so callers can surface real errors (no silent null).
 */
export async function getAuthenticatedActor(
	identity: Identity,
): Promise<AuthenticatedActorResult | null> {
	if (!hasProtocolEnv()) {
		lastAttachError =
			"Protocol canister ID not baked into SPA — rebuild after start-local writes .env.local";
		return null;
	}
	if (!identity || identity.getPrincipal().isAnonymous()) {
		lastAttachError = "Identity is anonymous — Connect with Internet Identity";
		return null;
	}
	try {
		const config = getIcConfig();
		if (!config.protocolCanisterId?.trim()) {
			lastAttachError = "protocolCanisterId empty in config";
			return null;
		}
		const agent = await createHttpAgent(config, identity);
		const actor = createProtocolActor(config.protocolCanisterId, agent);
		lastAttachError = null;
		return { agent, actor, config };
	} catch (e) {
		console.error("[motokomaker-v2] authenticated protocol actor failed:", e);
		lastAttachError = formatAttachError(e);
		return null;
	}
}

export function natToAmount(n: bigint | number, decimals = 8): number {
	const v = typeof n === "bigint" ? Number(n) : n;
	return v / 10 ** decimals;
}

export function amountToNat(amount: number, decimals = 8): bigint {
	return BigInt(Math.round(amount * 10 ** decimals));
}
